import { Box, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useMemo, useState } from "react";

import { GenericTableMobileCards } from "@/components/GenericTable/GenericTableMobileCards";
import type { TableColumn } from "@/components/GenericTable/types";
import { MobileBottomSheet } from "@/components/common/MobileBottomSheet";
import { DashViewAllButton } from "@/pages/home/components/PatientDash/components/DashViewAllButton";

import { ConsentActions } from "./ConsentActions";
import {
  enrichConsentRowForMobileCard,
  getConsentTableColumns,
} from "./consentTableColumns";

const CONSENT_PREVIEW_LIMIT = 5;

interface ConsentMobileListProps {
  sectionTitle: string;
  data: any[];
  type: string;
  selectedTab: string;
}

/**
 * Carrossel mobile igual ao DependentList (`GenericTableMobileCards`)
 * com preview de 5 itens e "Ver tudo" para lista vertical completa.
 */
export function ConsentMobileList({
  sectionTitle,
  data,
  type,
  selectedTab,
}: ConsentMobileListProps) {
  const [allListOpen, setAllListOpen] = useState(false);

  const tableColumns = useMemo(
    (): TableColumn[] =>
      getConsentTableColumns(selectedTab, type, { includeMobileStatus: true }),
    [selectedTab, type],
  );

  const sortedRows = useMemo(() => {
    const rows = Array.isArray(data) ? [...data] : [];
    return rows
      .sort((a, b) => {
        const dateA = dayjs(a?.fullDate);
        const dateB = dayjs(b?.fullDate);
        return dateB.diff(dateA);
      })
      .map((row) => enrichConsentRowForMobileCard(row));
  }, [data]);

  const previewRows = useMemo(
    () => sortedRows.slice(0, CONSENT_PREVIEW_LIMIT),
    [sortedRows],
  );

  if (sortedRows.length === 0) {
    return (
      <Box sx={{ py: 1.5 }}>
        <Typography variant="body2" color="text.disabled" align="center">
          Nenhum registro em {sectionTitle.toLowerCase()}.
        </Typography>
      </Box>
    );
  }

  const showVerTudo = sortedRows.length > CONSENT_PREVIEW_LIMIT;

  const renderActions = (rowData: Record<string, unknown>) => (
    <ConsentActions row={rowData} type={type} />
  );

  return (
    <>
      <GenericTableMobileCards
        tableColumns={tableColumns}
        tableRows={previewRows}
        actions={renderActions}
      />

      {showVerTudo ? (
        <DashViewAllButton
          onClick={() => setAllListOpen(true)}
          count={sortedRows.length}
        />
      ) : null}

      <MobileBottomSheet
        open={allListOpen}
        onClose={() => setAllListOpen(false)}
        title={`${sectionTitle} — Todos`}
      >
        <Stack spacing={0} sx={{ pb: 1 }}>
          <GenericTableMobileCards
            mobileLayout="vertical"
            tableColumns={tableColumns}
            tableRows={sortedRows}
            actions={renderActions}
          />
        </Stack>
      </MobileBottomSheet>
    </>
  );
}
